import React, { lazy, Suspense } from 'react';

import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import Load from './atomicDesign/molecules/Load';
import Error404 from './components/Error404';
import HelpCenter from './pages/public/HelpCenter';
import Main from './pages/public/HelpCenter/Main';
import PdfViewer from './pages/admin/PdfViewer/PdfViewer';
const FileItems = lazy(() => import('./pages/admin/Settings/FileItems'));
import UserAuditLogs from './pages/admin/Settings/Users/UserAuditLogs';
import PublicDisplay from './pages/PublicDisplay/PublicDisplay';
import PublicDisplayLogin from './components/PublicDisplayLogin';
import SupportDashboard from './pages/public/HelpCenter/Sections/SupportDashboard';
import BuildingDashboard from './pages/public/HelpCenter/Sections/BuildingDashboard';
import DeviceDashboard from './pages/public/HelpCenter/Sections/DeviceDashboard';
import SettingsDashboard from './pages/public/HelpCenter/Sections/SettingsDashboard';
import MapDashboard from './pages/public/HelpCenter/Sections/MapDashboard';
import GeneralSiteInspection from './pages/admin/SiteInspection/GeneralSiteInspection';
import Baseline from './pages/admin/Settings/Baseline';
const OperatingCalendars = lazy(
  () => import('./pages/admin/Settings/OperatingCalendars')
);
const OperatingCalendarEdit = lazy(
  () => import('./pages/admin/Settings/OperatingCalendars/Edit')
);
const OperatingCalendarView = lazy(
  () => import('./pages/admin/Settings/OperatingCalendars/Calendar')
);
const Setpoints = lazy(() => import('./pages/admin/Settings/Setpoints'));
const SetpointEdit = lazy(
  () => import('./pages/admin/Settings/Setpoints/Edit')
);
import { energyLabelFormRoutes } from './pages/EnergyLabelForm/routes';

/* 3d model test */
const Model3D = lazy(() => import('./pages/3d'));
const SiteInspectionActions = lazy(
  () => import('./pages/admin/SuperSettings/SiteInspections/Actions')
);
const Chat = lazy(() => import('./pages/admin/Chat'));

const SuperSettings = lazy(() => import('./pages/admin/SuperSettings'));
const DeviceOverview = lazy(() => import('./pages/admin/DeviceOverview'));
const BuildingZones = lazy(() => import('./pages/admin/Building/Zone'));
const BuildingASPA = lazy(() => import('./pages/admin/BuildingASPA'));
const ModelViewer = lazy(() => import('./pages/admin/ModelViewer'));
const WorldMap = lazy(() => import('./pages/admin/Map'));
const EnergyPriceSettings = lazy(
  () => import('./pages/admin/Settings/EnergyPriceSettings')
);
/* Settings Elements */
const Settings = lazy(() => import('./pages/admin/Settings'));
const Devices = lazy(() => import('./pages/admin/Settings/Devices'));
const EosDevices = lazy(() => import('./pages/admin/Settings/EosDevices'));
const DeviceAlarms = lazy(
  () => import('./pages/admin/SuperSettings/DeviceAlarms')
);
const AlarmSystems = lazy(
  () => import('./pages/admin/SuperSettings/AlarmSystems')
);

const Themes = lazy(() => import('./pages/admin/Settings/Themes'));
const Categories = lazy(() => import('./pages/admin/Settings/Categories'));
const Users = lazy(() => import('./pages/admin/Settings/Users'));
const UserCalendar = lazy(() => import('./pages/admin/UserCalendar'));
const VendorEdit = lazy(() => import('./pages/admin/Settings/VendorEdit'));

// Building Automation (SASTECH)
const TechnicalRoom = lazy(() => import('./pages/admin/TechnicalRoom'));
const TechnicalRoomEditor = lazy(
  () => import('./pages/admin/TechnicalRoom/Editor')
);
const EosDash = lazy(() => import('./pages/admin/EosDash'));
const BuildingView = lazy(() => import('./pages/admin/Building'));
const ZoneEdit = lazy(() => import('./pages/admin/Zone/ZoneEdit'));
const Zone = lazy(() => import('./pages/admin/Zone'));
const Device = lazy(() => import('./pages/admin/Device'));
const EosDevice = lazy(() => import('./pages/admin/EosDevice'));
const VisualToggleDeviceEdit = lazy(
  () =>
    import('./pages/admin/Settings/Devices/VisualToggleDevice/EditVisualToggleDevice')
);
const AspaSetup = lazy(() => import('./pages/admin/Settings/AspaSetup'));
const ControlStrategySettings = lazy(
  () => import('./pages/admin/Settings/ControlStrategy')
);
const Aspa = lazy(() => import('./pages/admin/Building/Aspa'));
const SiteInspectionAdmin = lazy(() => import('./pages/admin/SiteInspection'));
const EnergyLabelLeads = lazy(() => import('./pages/admin/EnergyLabelLeads'));
const EnergyLabelLeadDetail = lazy(
  () => import('./pages/admin/EnergyLabelLeads/Detail')
);

const EnergyLabelLeadSubmission = lazy(
  () => import('./pages/admin/EnergyLabelLeads/Submission')
);
const Reports = lazy(() => import('./pages/admin/Reports'));
const EditSiteInspection = lazy(
  () => import('./pages/admin/SiteInspection/EditSiteInspection')
);
const NetmoreInstaller = lazy(
  () => import('./pages/admin/Settings/Devices/NetmoreInstaller')
);
const Report = lazy(
  () => import('./pages/admin/SuperSettings/SiteInspections/Report')
);
const BuildingInspectionList = lazy(
  () => import('./pages/admin/BuildingInspection/pages/List')
);
const InspectionLayout = lazy(
  () => import('./pages/admin/BuildingInspection/Layout')
);
const InspectionInfo = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Info')
);
const ThermalZonesList = lazy(
  () => import('./pages/admin/BuildingInspection/pages/ThermalZones/List')
);
const ThermalZoneEdit = lazy(
  () => import('./pages/admin/BuildingInspection/pages/ThermalZones/Edit')
);
const ZoneFloor = lazy(
  () => import('./pages/admin/BuildingInspection/pages/ThermalZones/Floor')
);
const ZoneRoof = lazy(
  () => import('./pages/admin/BuildingInspection/pages/ThermalZones/Roof')
);
const ZoneBasement = lazy(
  () => import('./pages/admin/BuildingInspection/pages/ThermalZones/Basement')
);
const ZoneThermalMass = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ThermalMass')
);
const ZoneGroundConditions = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/GroundConditions')
);
const ZoneInternalLoads = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/InternalLoads')
);
const ExteriorWallsList = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ExteriorWalls/List')
);
const ExteriorWallEdit = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ExteriorWalls/Edit')
);
const WindowsList = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ExteriorWalls/Windows/List')
);
const WindowsEdit = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ExteriorWalls/Windows/Edit')
);
const DoorsList = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ExteriorWalls/Doors/List')
);
const DoorsEdit = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ExteriorWalls/Doors/Edit')
);
const GatesList = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ExteriorWalls/Gates/List')
);
const GatesEdit = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ExteriorWalls/Gates/Edit')
);
const ProductionEquipmentList = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ProductionEquipment/List')
);
const ProductionEquipmentEdit = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/ProductionEquipment/Edit')
);
const HvacList = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Hvac/List')
);
const HvacEdit = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Hvac/Edit')
);
const HeatingCoolingList = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/HeatingCooling/List')
);
const HeatingCoolingEdit = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/HeatingCooling/Edit')
);
const DomesticHotWaterList = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/DomesticHotWater/List')
);
const DomesticHotWaterEdit = lazy(
  () =>
    import('./pages/admin/BuildingInspection/pages/ThermalZones/DomesticHotWater/Edit')
);
const CentralEnergyList = lazy(
  () => import('./pages/admin/BuildingInspection/pages/CentralEnergy/List')
);
const CentralEnergyEdit = lazy(
  () => import('./pages/admin/BuildingInspection/pages/CentralEnergy/Edit')
);
const ExcessEnergyList = lazy(
  () => import('./pages/admin/BuildingInspection/pages/ExcessEnergy/List')
);
const ExcessEnergyEdit = lazy(
  () => import('./pages/admin/BuildingInspection/pages/ExcessEnergy/Edit')
);
const SolarPVList = lazy(
  () => import('./pages/admin/BuildingInspection/pages/SolarPV/List')
);
const SolarPVEdit = lazy(
  () => import('./pages/admin/BuildingInspection/pages/SolarPV/Edit')
);
const BatteriesList = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Batteries/List')
);
const BatteryEdit = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Batteries/Edit')
);
const InspectionAttachments = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Attachments')
);
const InspectionSimulation = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Simulation')
);
const InspectionEnergyLabel = lazy(
  () => import('./pages/admin/BuildingInspection/pages/EnergyLabel')
);
const InspectionSimulationZone = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Simulation/Zone')
);
const InspectionEnergyLabelZone = lazy(
  () => import('./pages/admin/BuildingInspection/pages/EnergyLabel/Zone')
);
const BuildingClimate = lazy(() => import('./pages/admin/BuildingClimate'));
const ScenarioPage = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Scenario')
);
const CostSavings = lazy(
  () => import('./pages/admin/BuildingInspection/pages/CostSavings')
);
const InspectionReport = lazy(
  () => import('./pages/admin/BuildingInspection/pages/Report')
);

const QuestionnaireListOfTemplates = lazy(
  () =>
    import('./pages/admin/SuperSettings/QuestionaireCreator/QuestionaireListOfTemplates')
);
const PublicQuestionaire = lazy(
  () => import('./pages/publicQuestionaire/index')
);
const EnergyLabelForm = lazy(() => import('./pages/EnergyLabelForm'));

const Admin = lazy(() => import('./components/Admin'));
const OpenApi = lazy(() => import('./pages/public/OpenApi'));

/* - End of Admin SubPages */

const MainIndex = lazy(() => import('./pages/public/Index'));

// THIS IS THE DEMO
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const Solar = lazy(() => import('./pages/admin/Solar'));
const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <Error404 />,
    element: (
      <Suspense fallback={<Load />}>
        <MainIndex />
      </Suspense>
    ),
    children: [
      {
        path: '/open-api',
        element: (
          <Suspense fallback={<Load />}>
            <OpenApi />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/publicQuestionaire',
    errorElement: <Error404 />,
    element: (
      <Suspense fallback={<Load />}>
        <PublicQuestionaire />
      </Suspense>
    ),
  },
  {
    path: '/energy-label-form/:submissionId?',
    errorElement: <Error404 />,
    element: (
      <Suspense fallback={<Load />}>
        <EnergyLabelForm />
      </Suspense>
    ),
    children: energyLabelFormRoutes,
  },
  {
    path: '/admin',
    errorElement: <Error404 />,
    element: (
      <Suspense fallback={<Load />}>
        <Admin />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Load />}>
            <EosDash />
          </Suspense>
        ),
      },
      {
        path: '/admin/3d',
        element: (
          <Suspense fallback={<Load />}>
            <Model3D />
          </Suspense>
        ),
      },
      {
        path: '/admin/site-inspection',
        element: (
          <Suspense fallback={<Load />}>
            <SiteInspectionAdmin />
          </Suspense>
        ),
      },
      {
        path: '/admin/user-calendar',
        element: (
          <Suspense fallback={<Load />}>
            <UserCalendar />
          </Suspense>
        ),
      },
      {
        path: '/admin/energy-label-leads',
        element: (
          <Suspense fallback={<Load />}>
            <EnergyLabelLeads />
          </Suspense>
        ),
      },
      {
        path: '/admin/energy-label-leads/:id',
        element: (
          <Suspense fallback={<Load />}>
            <EnergyLabelLeadDetail />
          </Suspense>
        ),
      },
      {
        path: '/admin/energy-label-leads/:id/submission',
        element: (
          <Suspense fallback={<Load />}>
            <EnergyLabelLeadSubmission />
          </Suspense>
        ),
      },
      {
        path: '/admin/reports',
        element: (
          <Suspense fallback={<Load />}>
            <Reports />
          </Suspense>
        ),
      },
      {
        path: '/admin/site-inspection/:id',
        element: (
          <Suspense fallback={<Load />}>
            <EditSiteInspection />
          </Suspense>
        ),
        children: [
          {
            index: true,
            path: '/admin/site-inspection/:id/edit',
            element: (
              <Suspense fallback={<Load />}>
                <GeneralSiteInspection />
              </Suspense>
            ),
          },
          {
            path: '/admin/site-inspection/:id/actions',
            element: (
              <Suspense fallback={<Load />}>
                <SiteInspectionActions />
              </Suspense>
            ),
          },
          {
            path: '/admin/site-inspection/:id/report',
            element: (
              <Suspense fallback={<Load />}>
                <Report />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: '/admin/building-inspection',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Load />}>
                <BuildingInspectionList />
              </Suspense>
            ),
          },
          {
            path: ':id',
            element: (
              <Suspense fallback={<Load />}>
                <InspectionLayout />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Load />}>
                    <InspectionInfo />
                  </Suspense>
                ),
              },
              {
                path: 'zones',
                element: (
                  <Suspense fallback={<Load />}>
                    <ThermalZonesList />
                  </Suspense>
                ),
              },
              {
                path: 'hvac',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Load />}>
                        <HvacList />
                      </Suspense>
                    ),
                  },
                  {
                    path: ':hvacId',
                    element: (
                      <Suspense fallback={<Load />}>
                        <HvacEdit />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'central-energy',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Load />}>
                        <CentralEnergyList />
                      </Suspense>
                    ),
                  },
                  {
                    path: ':moduleId',
                    element: (
                      <Suspense fallback={<Load />}>
                        <CentralEnergyEdit />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'excess-energy',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Load />}>
                        <ExcessEnergyList />
                      </Suspense>
                    ),
                  },
                  {
                    path: ':moduleId',
                    element: (
                      <Suspense fallback={<Load />}>
                        <ExcessEnergyEdit />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'solar-pv',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Load />}>
                        <SolarPVList />
                      </Suspense>
                    ),
                  },
                  {
                    path: ':moduleId',
                    element: (
                      <Suspense fallback={<Load />}>
                        <SolarPVEdit />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'batteries',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Load />}>
                        <BatteriesList />
                      </Suspense>
                    ),
                  },
                  {
                    path: ':batteryId',
                    element: (
                      <Suspense fallback={<Load />}>
                        <BatteryEdit />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: 'attachments',
                element: (
                  <Suspense fallback={<Load />}>
                    <InspectionAttachments />
                  </Suspense>
                ),
              },
              {
                path: 'simulation',
                element: (
                  <Suspense fallback={<Load />}>
                    <InspectionSimulation />
                  </Suspense>
                ),
              },
              {
                path: 'simulation/zone/:zoneId',
                element: (
                  <Suspense fallback={<Load />}>
                    <InspectionSimulationZone />
                  </Suspense>
                ),
              },
              {
                path: 'energy-label',
                element: (
                  <Suspense fallback={<Load />}>
                    <InspectionEnergyLabel />
                  </Suspense>
                ),
              },
              {
                path: 'energy-label/zone/:zoneId',
                element: (
                  <Suspense fallback={<Load />}>
                    <InspectionEnergyLabelZone />
                  </Suspense>
                ),
              },
              {
                path: 'scenario',
                element: (
                  <Suspense fallback={<Load />}>
                    <ScenarioPage />
                  </Suspense>
                ),
              },
              {
                path: 'cost-savings',
                element: (
                  <Suspense fallback={<Load />}>
                    <CostSavings />
                  </Suspense>
                ),
              },
              {
                path: 'report',
                element: (
                  <Suspense fallback={<Load />}>
                    <InspectionReport />
                  </Suspense>
                ),
              },
              {
                path: 'zones/:zoneId',
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Load />}>
                        <ThermalZoneEdit />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'floor',
                    element: (
                      <Suspense fallback={<Load />}>
                        <ZoneFloor />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'roof',
                    element: (
                      <Suspense fallback={<Load />}>
                        <ZoneRoof />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'basement',
                    element: (
                      <Suspense fallback={<Load />}>
                        <ZoneBasement />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'thermal-mass',
                    element: (
                      <Suspense fallback={<Load />}>
                        <ZoneThermalMass />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'ground',
                    element: (
                      <Suspense fallback={<Load />}>
                        <ZoneGroundConditions />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'loads',
                    element: (
                      <Suspense fallback={<Load />}>
                        <ZoneInternalLoads />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'walls',
                    element: (
                      <Suspense fallback={<Load />}>
                        <ExteriorWallsList />
                      </Suspense>
                    ),
                  },
                  {
                    path: 'walls/:wallId',
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<Load />}>
                            <ExteriorWallEdit />
                          </Suspense>
                        ),
                      },
                      {
                        path: 'windows',
                        children: [
                          {
                            index: true,
                            element: (
                              <Suspense fallback={<Load />}>
                                <WindowsList />
                              </Suspense>
                            ),
                          },
                          {
                            path: ':windowId',
                            element: (
                              <Suspense fallback={<Load />}>
                                <WindowsEdit />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        path: 'doors',
                        children: [
                          {
                            index: true,
                            element: (
                              <Suspense fallback={<Load />}>
                                <DoorsList />
                              </Suspense>
                            ),
                          },
                          {
                            path: ':doorId',
                            element: (
                              <Suspense fallback={<Load />}>
                                <DoorsEdit />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                      {
                        path: 'gates',
                        children: [
                          {
                            index: true,
                            element: (
                              <Suspense fallback={<Load />}>
                                <GatesList />
                              </Suspense>
                            ),
                          },
                          {
                            path: ':gateId',
                            element: (
                              <Suspense fallback={<Load />}>
                                <GatesEdit />
                              </Suspense>
                            ),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'equipment',
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<Load />}>
                            <ProductionEquipmentList />
                          </Suspense>
                        ),
                      },
                      {
                        path: ':equipmentId',
                        element: (
                          <Suspense fallback={<Load />}>
                            <ProductionEquipmentEdit />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'heating-cooling',
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<Load />}>
                            <HeatingCoolingList />
                          </Suspense>
                        ),
                      },
                      {
                        path: ':moduleId',
                        element: (
                          <Suspense fallback={<Load />}>
                            <HeatingCoolingEdit />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'domestic-hot-water',
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<Load />}>
                            <DomesticHotWaterList />
                          </Suspense>
                        ),
                      },
                      {
                        path: ':dhwId',
                        element: (
                          <Suspense fallback={<Load />}>
                            <DomesticHotWaterEdit />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/admin/eos',
        element: (
          <Suspense fallback={<Load />}>
            <EosDash />
          </Suspense>
        ),
      },
      {
        path: '/admin/device/:deviceId',
        element: (
          <Suspense fallback={<Load />}>
            <Device />
          </Suspense>
        ),
      },
      {
        path: '/admin/eos-device/:deviceId',
        element: (
          <Suspense fallback={<Load />}>
            <EosDevice />
          </Suspense>
        ),
      },
      {
        path: '/admin/devices',
        element: (
          <Suspense fallback={<Load />}>
            <DeviceOverview />
          </Suspense>
        ),
      },
      {
        path: '/admin/building-ASPA',
        element: (
          <Suspense fallback={<Load />}>
            <BuildingASPA />
          </Suspense>
        ),
      },
      {
        path: '/admin/analytics',
        element: (
          <Suspense fallback={<Load />}>
            <Analytics />
          </Suspense>
        ),
      },
      {
        path: '/admin/solar',
        element: (
          <Suspense fallback={<Load />}>
            <Solar />
          </Suspense>
        ),
      },
      {
        path: '/admin/report',
        element: (
          <Suspense fallback={<Load />}>
            <Report />
          </Suspense>
        ),
      },
      {
        path: '/admin/technicalroom',
        element: (
          <Suspense fallback={<Load />}>
            <TechnicalRoom />
          </Suspense>
        ),
      },
      {
        path: '/admin/technicalroom/:id',
        element: (
          <Suspense fallback={<Load />}>
            <TechnicalRoomEditor />
          </Suspense>
        ),
      },
      {
        path: '/admin/questionaireanswers',
        element: (
          <Suspense fallback={<Load />}>
            <QuestionnaireListOfTemplates />
          </Suspense>
        ),
      },
      {
        path: '/admin/model-viewer',
        element: (
          <Suspense fallback={<Load />}>
            <ModelViewer />
          </Suspense>
        ),
      },
      {
        path: '/admin/building/:id',
        element: (
          <Suspense fallback={<Load />}>
            <BuildingView />
          </Suspense>
        ),
      },
      {
        path: '/admin/building/:id/aspa',
        element: (
          <Suspense fallback={<Load />}>
            <Aspa />
          </Suspense>
        ),
      },
      {
        path: '/admin/building/:id/zones',
        element: (
          <Suspense fallback={<Load />}>
            <BuildingZones />
          </Suspense>
        ),
      },
      {
        path: '/admin/building/:id/zones/:zoneId',
        element: (
          <Suspense fallback={<Load />}>
            <Zone />
          </Suspense>
        ),
      },
      {
        path: '/admin/climate/zone/:id',
        element: (
          <Suspense fallback={<Load />}>
            <BuildingClimate />
          </Suspense>
        ),
      },
      {
        path: '/admin/chat',
        element: (
          <Suspense fallback={<Load />}>
            <Chat />
          </Suspense>
        ),
      },
      {
        path: '/admin/pdfViewer',
        element: (
          <Suspense fallback={<Load />}>
            <PdfViewer />
          </Suspense>
        ),
      },
      {
        path: '/admin/zone/:id/edit',
        element: (
          <Suspense fallback={<Load />}>
            <ZoneEdit />
          </Suspense>
        ),
      },
      {
        path: '/admin/map',
        element: (
          <Suspense fallback={<Load />}>
            <WorldMap />
          </Suspense>
        ),
      },
      {
        path: '/admin/settings',
        element: (
          <Suspense fallback={<Load />}>
            <Settings />
          </Suspense>
        ),
        children: [
          {
            default: true,
            path: '/admin/settings/vendor',
            element: (
              <Suspense fallback={<Load />}>
                <VendorEdit />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/devices/:id?',
            element: (
              <Suspense fallback={<Load />}>
                <Devices />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/eos-devices/:id?',
            element: (
              <Suspense fallback={<Load />}>
                <EosDevices />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/device-alarms/:deviceId?',
            element: (
              <Suspense fallback={<Load />}>
                <DeviceAlarms />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/alarms',
            element: (
              <Suspense fallback={<Load />}>
                <AlarmSystems />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/aspa-setup',
            element: (
              <Suspense fallback={<Load />}>
                <AspaSetup />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/control-strategy',
            element: (
              <Suspense fallback={<Load />}>
                <ControlStrategySettings />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/operating-calendar',
            element: (
              <Suspense fallback={<Load />}>
                <OperatingCalendars />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/operating-calendar/new',
            element: (
              <Suspense fallback={<Load />}>
                <OperatingCalendarEdit />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/operating-calendar/:id',
            element: (
              <Suspense fallback={<Load />}>
                <OperatingCalendarView />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/operating-calendar/:id/edit',
            element: (
              <Suspense fallback={<Load />}>
                <OperatingCalendarEdit />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/setpoint',
            element: (
              <Suspense fallback={<Load />}>
                <Setpoints />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/setpoint/new',
            element: (
              <Suspense fallback={<Load />}>
                <SetpointEdit />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/setpoint/:id/edit',
            element: (
              <Suspense fallback={<Load />}>
                <SetpointEdit />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/themes',
            element: (
              <Suspense fallback={<Load />}>
                <Themes />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/categories',
            element: (
              <Suspense fallback={<Load />}>
                <Categories />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/users',
            element: (
              <Suspense fallback={<Load />}>
                <Users />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/users/:userId/audit',
            element: (
              <Suspense fallback={<Load />}>
                <UserAuditLogs />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/file-items',
            element: (
              <Suspense fallback={<Load />}>
                <FileItems />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/energy-price',
            element: (
              <Suspense fallback={<Load />}>
                <EnergyPriceSettings />
              </Suspense>
            ),
          },
          {
            path: '/admin/settings/baseline',
            element: (
              <Suspense fallback={<Load />}>
                <Baseline />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: '/admin/superSettings',
        element: (
          <Suspense fallback={<Load />}>
            <SuperSettings />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/help-center',
    element: <HelpCenter />,
    errorElement: <Error404 />,
    children: [
      {
        path: '/help-center',
        index: true,
        element: (
          <Suspense fallback={<Load />}>
            <Main />
          </Suspense>
        ),
      },
      {
        path: '/help-center/dashboard',
        index: true,
        element: (
          <Suspense fallback={<Load />}>
            <SupportDashboard />
          </Suspense>
        ),
      },
      {
        path: '/help-center/building',
        index: true,
        element: (
          <Suspense fallback={<Load />}>
            <BuildingDashboard />
          </Suspense>
        ),
      },
      {
        path: '/help-center/devices',
        index: true,
        element: (
          <Suspense fallback={<Load />}>
            <DeviceDashboard />
          </Suspense>
        ),
      },
      {
        path: '/help-center/settings',
        index: true,
        element: (
          <Suspense fallback={<Load />}>
            <SettingsDashboard />
          </Suspense>
        ),
      },
      {
        path: '/help-center/map',
        index: true,
        element: (
          <Suspense fallback={<Load />}>
            <MapDashboard />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: '/public-display/:token',
    element: <PublicDisplayLogin />,
  },
  {
    path: '/public-display-view/:token',
    element: <PublicDisplay />,
  },
]);

function Routes() {
  return <RouterProvider router={router} />;
}

export default Routes;
